import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { useI18n } from '../../../i18n/I18nContext'
import { createPortal } from 'react-dom'
import type { PaginationMeta, SortDirection } from '../../../types/access'
import type { InterfaceDensity } from '../../../services/interfacePreferences'
import { navigateTo } from '../../../services/navigation'

type DialogSize = 'sm' | 'md' | 'lg' | 'xl'
type AdminPageWidth = 'readable' | 'wide' | 'full'
export type AdminStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'inactive' | 'pending' | 'permission-limited'

interface DialogShellProps {
  title: string
  description?: string
  isOpen: boolean
  onClose: (reason?: 'escape' | 'button' | 'backdrop') => void
  children: React.ReactNode
  footer?: React.ReactNode
  size?: DialogSize
  labelledById?: string
  closeOnBackdrop?: boolean
}

export type AdminRowAction = {
  id: string
  label: string
  onSelect: () => void
  disabled?: boolean
  disabledReason?: string
  destructive?: boolean
}

const sizeClass = (size: DialogSize = 'md') => `admin-modal-card admin-modal-card--${size}`

export const AdminPage: React.FC<{
  children: React.ReactNode
  width?: AdminPageWidth
  className?: string
  testId?: string
}> = ({ children, width = 'wide', className = '', testId }) => (
  <div className={`admin-page admin-page--${width} ${className}`.trim()} data-testid={testId}>
    {children}
  </div>
)

export const AdminPageHeader: React.FC<{
  title: string
  description?: string
  context?: React.ReactNode
  primaryAction?: React.ReactNode
  secondaryActions?: React.ReactNode
  status?: React.ReactNode
  titleId?: string
  headingLevel?: 1 | 2
  breadcrumbs?: Array<{ label: string; href?: string }>
  breadcrumbLabel?: string
}> = ({ title, description, context, primaryAction, secondaryActions, status, titleId, headingLevel = 1, breadcrumbs, breadcrumbLabel = 'Page hierarchy' }) => {
  const generatedId = useId()
  const headingId = titleId || `admin-page-title-${generatedId}`
  const Heading = headingLevel === 2 ? 'h2' : 'h1'
  return (
    <header className="admin-page-header" aria-labelledby={headingId} data-breadcrumbs={breadcrumbs?.length ? 'true' : undefined}>
      <div className="admin-page-header__copy">
        {breadcrumbs?.length ? <nav className="admin-page-header__breadcrumbs" aria-label={breadcrumbLabel}>
          <ol>{breadcrumbs.map((crumb, index) => <li key={`${index}:${crumb.label}`}>
            {index < breadcrumbs.length - 1 && crumb.href ? <a href={crumb.href} onClick={event => {
              if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
              // Only intercept same-origin links; external links keep native behavior.
              if (new URL(crumb.href!, window.location.href).origin !== window.location.origin) return
              event.preventDefault()
              navigateTo(crumb.href!)
            }}>{crumb.label}</a> : <span aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}>{crumb.label}</span>}
          </li>)}</ol>
        </nav> : null}
        {context ? <div className="admin-page-header__context">{context}</div> : null}
        <div className="admin-page-header__title-row">
          <Heading id={headingId}>{title}</Heading>
          {status ? <div className="admin-page-header__status">{status}</div> : null}
        </div>
        {description ? <p>{description}</p> : null}
      </div>
      {(primaryAction || secondaryActions) ? (
        <div className="admin-page-header__actions">
          {secondaryActions ? <div className="admin-page-header__secondary">{secondaryActions}</div> : null}
          {primaryAction ? <div className="admin-page-header__primary">{primaryAction}</div> : null}
        </div>
      ) : null}
    </header>
  )
}

export const ErrorBanner: React.FC<{ message?: string; children?: React.ReactNode; testId?: string }> = ({ message, children, testId }) => {
  if (!message && !children) return null
  return <div className="admin-error-banner" role="alert" data-testid={testId}>{message || children}</div>
}

export const LoadingState: React.FC<{ label?: string }> = ({ label = 'Loading...' }) => (
  <div className="admin-state admin-state--loading" role="status">{label}</div>
)

export const EmptyState: React.FC<{
  title: string
  description?: string
  action?: React.ReactNode
  variant?: 'default' | 'permission-limited' | 'not-connected' | 'filtered'
}> = ({ title, description, action, variant = 'default' }) => (
  <div className={`admin-state admin-state--empty admin-state--${variant}`}>
    <strong>{title}</strong>
    {description ? <p>{description}</p> : null}
    {action ? <div>{action}</div> : null}
  </div>
)

export const disabledReason = (allowed: boolean, reason: string) => (allowed ? undefined : reason)

export const PermissionHint: React.FC<{ allowed: boolean; reason: string }> = ({ allowed, reason }) => (
  allowed ? null : <p className="helper">{reason}</p>
)

export const FormModal: React.FC<DialogShellProps> = ({
  title,
  description,
  isOpen,
  onClose,
  children,
  footer,
  size = 'md',
  labelledById,
  closeOnBackdrop = true,
}) => {
  const cardRef = useRef<HTMLElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!isOpen) return undefined
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const frame = window.requestAnimationFrame(() => closeButtonRef.current?.focus())
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current('escape')
        return
      }
      if (event.key !== 'Tab') return
      const card = cardRef.current
      if (!card) return
      const focusable = Array.from(card.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => el.offsetParent !== null)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', handleKeyDown)
      returnFocusRef.current?.focus()
    }
  }, [isOpen])

  if (!isOpen) return null
  const titleId = labelledById || `admin-modal-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
  return (
    <div className="admin-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (closeOnBackdrop && event.target === event.currentTarget) onClose('backdrop')
    }}>
      <section
        className={sizeClass(size)}
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="admin-modal-header">
          <div>
            <h3 id={titleId}>{title}</h3>
            {description ? <p>{description}</p> : null}
          </div>
          <button type="button" ref={closeButtonRef} className="btn btn-ghost admin-icon-button" onClick={() => onClose('button')} aria-label="Close">x</button>
        </header>
        <div className="admin-modal-body">{children}</div>
        {footer ? <footer className="admin-modal-footer">{footer}</footer> : null}
      </section>
    </div>
  )
}

export const ConfirmDialog: React.FC<{
  title: string
  message: string
  isOpen: boolean
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  disabled?: boolean
  onCancel: () => void
  onConfirm: () => void
}> = ({
  title,
  message,
  isOpen,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  disabled = false,
  onCancel,
  onConfirm,
}) => (
  <FormModal
    title={title}
    isOpen={isOpen}
    onClose={onCancel}
    size="sm"
    footer={(
      <>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={disabled}>{cancelLabel}</button>
        <button type="button" className={destructive ? 'btn btn-danger' : 'btn btn-primary'} onClick={onConfirm} disabled={disabled}>{confirmLabel}</button>
      </>
    )}
  >
    <p className="text-muted">{message}</p>
  </FormModal>
)

export const DetailDrawer: React.FC<{
  title: string
  subtitle?: string
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  actions?: React.ReactNode
  nonBlocking?: boolean
}> = ({ title, subtitle, isOpen, onClose, children, actions, nonBlocking = false }) => {
  const titleId = useId()
  const drawerRef = useRef<HTMLElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isOpen) return undefined
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const frame = window.requestAnimationFrame(() => closeButtonRef.current?.focus())
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || nonBlocking) return
      const drawer = drawerRef.current
      if (!drawer) return
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => el.offsetParent !== null)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', handleKeyDown)
      returnFocusRef.current?.focus()
    }
  }, [isOpen, nonBlocking, onClose])

  if (!isOpen) return null
  return (
    <div
      className={`admin-drawer-backdrop ${nonBlocking ? 'admin-drawer-backdrop--non-blocking' : ''}`}
      role="presentation"
      onMouseDown={nonBlocking ? undefined : onClose}
    >
      <aside
        className="admin-detail-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal={nonBlocking ? 'false' : 'true'}
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="admin-drawer-header">
          <div>
            <h3 id={titleId}>{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" ref={closeButtonRef} className="btn btn-ghost admin-icon-button" onClick={onClose} aria-label="Close">x</button>
        </header>
        {actions ? <div className="admin-drawer-actions">{actions}</div> : null}
        <div className="admin-drawer-body">{children}</div>
      </aside>
    </div>
  )
}

export const AdminRowActionMenu: React.FC<{
  recordName: string
  actions: AdminRowAction[]
  label?: string
}> = ({ recordName, actions, label = 'Actions' }) => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const menuId = useId()
  const menuRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  // The menu renders through a portal with fixed positioning so table scroll
  // containers (overflow-x: auto) can never clip it or grow a nested scrollbar.
  const placeMenu = () => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const menuWidth = Math.max(menuRef.current?.offsetWidth || 0, 160)
    const menuHeight = menuRef.current?.offsetHeight || 0
    let left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)
    left = Math.max(8, left)
    let top = rect.bottom + 4
    if (menuHeight && top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, Math.min(rect.top - 4 - menuHeight, window.innerHeight - menuHeight - 8))
    }
    setPosition({ top, left })
  }

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return undefined
    }
    placeMenu()
    const frame = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus()
    })
    const handlePointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) setOpen(false)
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    const handleReposition = () => placeMenu()
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [open])

  const moveMenuFocus = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') || [])
    if (items.length === 0) return
    const currentIndex = items.indexOf(event.currentTarget)
    const focusItem = (index: number) => {
      event.preventDefault()
      items[(index + items.length) % items.length]?.focus()
    }
    if (event.key === 'ArrowDown') focusItem(currentIndex + 1)
    if (event.key === 'ArrowUp') focusItem(currentIndex - 1)
    if (event.key === 'Home') focusItem(0)
    if (event.key === 'End') focusItem(items.length - 1)
  }

  return (
    <div
      className={`admin-action-menu ${open ? 'is-open' : ''}`}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        ref={triggerRef}
        className="btn btn-ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${label} for ${recordName}`}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>
      {open ? createPortal(
        <div
          className="admin-action-menu__items admin-action-menu__items--portal"
          id={menuId}
          role="menu"
          aria-label={`${label} for ${recordName}`}
          ref={menuRef}
          style={{ top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? 'visible' : 'hidden' }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              className={action.destructive ? 'btn btn-ghost admin-action-menu__danger' : 'btn btn-ghost'}
              disabled={action.disabled}
              title={action.disabled ? action.disabledReason : undefined}
              onClick={() => {
                if (action.disabled) return
                setOpen(false)
                triggerRef.current?.focus()
                action.onSelect()
              }}
              onKeyDown={moveMenuFocus}
            >
              {action.label}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

export const EntityActionMenu: React.FC<{
  label?: string
  children: React.ReactNode
}> = ({ label = 'Actions', children }) => (
  <div
    className="admin-action-menu"
    onClick={(event) => event.stopPropagation()}
    onMouseDown={(event) => event.stopPropagation()}
  >
    <button type="button" className="btn btn-ghost">{label}</button>
    <div className="admin-action-menu__items">{children}</div>
  </div>
)

export const PageSizeSelect: React.FC<{ value: number; onChange: (value: number) => void }> = ({ value, onChange }) => (
  <label className="helper">Rows
    <select className="input" value={value} onChange={(event) => onChange(Number(event.target.value))}>
      {[10, 25, 50, 100].map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  </label>
)

export const PaginationControls: React.FC<{
  pagination: PaginationMeta
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}> = ({ pagination, onPageChange, onPageSizeChange }) => {
  const from = pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.pageSize) + 1
  const to = Math.min(pagination.total, pagination.page * pagination.pageSize)
  return (
    <div className="admin-list-controls">
      <span className="helper">Showing {from}-{to} of {pagination.total}</span>
      <PageSizeSelect value={pagination.pageSize} onChange={onPageSizeChange} />
      <button type="button" className="btn btn-ghost" disabled={!pagination.hasPreviousPage} onClick={() => onPageChange(pagination.page - 1)}>Previous</button>
      <span className="helper">Page {pagination.page} of {pagination.totalPages}</span>
      <button type="button" className="btn btn-ghost" disabled={!pagination.hasNextPage} onClick={() => onPageChange(pagination.page + 1)}>Next</button>
    </div>
  )
}

export const SortableHeader: React.FC<{
  label: string
  sortKey: string
  activeSortBy?: string
  sortDir?: SortDirection
  onSort: (sortKey: string) => void
}> = ({ label, sortKey, activeSortBy, sortDir, onSort }) => {
  const { t } = useI18n()
  return (
  <button type="button" className="admin-sort-header" onClick={() => onSort(sortKey)}>
    {label}{activeSortBy === sortKey ? ` ${t(sortDir === 'desc' ? 'down' : 'up')}` : ''}
  </button>
  )
}

export const FilterBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="admin-filter-bar">{children}</div>
)

export const AdminFilterBar: React.FC<{
  children: React.ReactNode
  advanced?: React.ReactNode
  resultCount?: number
  activeFilterCount?: number
  onClear?: () => void
  advancedInitiallyOpen?: boolean
  advancedButtonRef?: React.Ref<HTMLButtonElement>
  ariaLabel?: string
}> = ({ children, advanced, resultCount, activeFilterCount = 0, onClear, advancedInitiallyOpen = false, advancedButtonRef, ariaLabel = 'Filters' }) => {
  const { t } = useI18n()
  const [advancedOpen, setAdvancedOpen] = useState(advancedInitiallyOpen)
  return (
    <form className="admin-filter-bar admin-filter-bar--structured" aria-label={ariaLabel} onSubmit={(event) => event.preventDefault()}>
      <div className="admin-filter-bar__primary">
        {children}
        {advanced ? (
          <button ref={advancedButtonRef} type="button" className="btn btn-ghost" aria-expanded={advancedOpen} onClick={() => setAdvancedOpen((value) => !value)}>
            {t('Advanced')}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        ) : null}
        {onClear && activeFilterCount > 0 ? <button type="button" className="btn btn-ghost" onClick={onClear}>{t('Clear all')}</button> : null}
        {typeof resultCount === 'number' ? <span className="helper" aria-live="polite">{t('{count} results', { count: resultCount })}</span> : null}
      </div>
      {advanced && advancedOpen ? <div className="admin-filter-bar__advanced">{advanced}</div> : null}
    </form>
  )
}

export const AdminActiveFilterChips: React.FC<{
  filters: Array<{ id: string; label: string; onRemove: () => void }>
  onClear?: () => void
}> = ({ filters, onClear }) => {
  const { t } = useI18n()
  if (filters.length === 0) return null
  return (
    <div className="admin-active-filter-chips" aria-label={t('Active filters')}>
      {filters.map((filter) => (
        <button key={filter.id} type="button" className="admin-chip admin-chip--button" onClick={filter.onRemove}>
          {filter.label} <span aria-hidden="true">x</span>
        </button>
      ))}
      {onClear ? <button type="button" className="btn btn-ghost" onClick={onClear}>{t('Clear all')}</button> : null}
    </div>
  )
}

export const AdminTableContainer: React.FC<{
  children: React.ReactNode
  label: string
  loading?: boolean
  loadingLabel?: string
  error?: string
  empty?: React.ReactNode
  isEmpty?: boolean
  pagination?: React.ReactNode
  compact?: boolean
  density?: InterfaceDensity
}> = ({ children, label, loading = false, loadingLabel, error, empty, isEmpty = false, pagination, compact = false, density }) => (
  <section className={`admin-table-container ${compact ? 'admin-table-container--compact' : ''}`} aria-label={label} data-interface-density={density}>
    {loading ? <LoadingState label={loadingLabel} /> : null}
    {error ? <ErrorBanner message={error} /> : null}
    <div className="admin-table-container__scroller" tabIndex={0}>
      {children}
    </div>
    {isEmpty && empty ? <div className="admin-table-container__state">{empty}</div> : null}
    {pagination ? <div className="admin-table-container__pagination">{pagination}</div> : null}
  </section>
)

export const AdminStatusBadge: React.FC<{
  children: React.ReactNode
  tone?: AdminStatusTone
}> = ({ children, tone = 'neutral' }) => (
  <span className={`admin-status-badge admin-status-badge--${tone}`}>{children}</span>
)
